import { Heading } from '@chakra-ui/layout';
import { Box, Flex, Popover, PopoverContent, PopoverTrigger, Portal, Text, useBreakpointValue } from '@chakra-ui/react';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useRouter } from 'next/router';
import { FC, Fragment, useMemo } from 'react';

import { checkIsLive } from '@/components/ui/features/schedule/ScheduleCalendar/ScheduleCalendar.utils';
import { ListItemTooltip } from '@/components/ui/features/schedule/ScheduleListView/components/ScheduleCalendarTooltipLecturesList';
import { AddToCalendarSession } from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendarSession';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { ThinEventSession } from '@/resources/ThinEventSession';

interface IListItemProps {
  session: ThinEventSession;
  newTab: boolean;
  mutateSession: () => void;
}

export const ListItem: FC<IListItemProps> = ({ session, newTab = false, mutateSession }) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const { timeZone, eventId } = useQueryParams<{ timeZone: string; eventId: string }>({
    timeZone: StringParam,
    eventId: StringParam,
  });

  const router = useRouter();

  const type = session.posterOnly ? 'posters' : 'sessions';

  const startsAt = utcToZonedTime(session.startsAt, timeZone).toISOString();
  const endsAt = utcToZonedTime(session.endsAt, timeZone).toISOString();

  const isLive = useMemo(() => checkIsLive(session.startsAt, session.endsAt, timeZone), [session, timeZone]);

  function handleSessionClick() {
    const url = session.url || `/events/${eventId}/${type}?eventSessionId=${session?.id}`;

    if (newTab) {
      window.open(url, '_blank');
    } else {
      router.push(url);
    }
  }

  return (
    <Popover
      arrowPadding={15}
      strategy="fixed"
      placement="bottom"
      openDelay={500}
      isLazy
      flip
      preventOverflow
      lazyBehavior="unmount"
      trigger="hover"
    >
      <PopoverTrigger>
        <Box cursor="pointer" onClick={handleSessionClick}>
          <Flex
            justifyContent="space-between"
            flexDir={{ base: 'column', lg: 'row' }}
            w="full"
            py={3}
            px={{ base: 4, lg: 10 }}
            _hover={{ bgColor: 'white', boxShadow: '0px 0px 16px 0px #0000001A' }}
          >
            <Text flex={1} fontFamily="heading" fontSize="xl" fontWeight="bold">
              {session.name}
            </Text>
            <Flex
              flex={1}
              w="full"
              justifyContent="space-between"
              flexDir={{ base: 'row-reverse', lg: 'row' }}
              mt={{ base: 2, lg: 0 }}
            >
              <Flex alignItems="center" justifyContent="center">
                <Box position="relative" w={6} h={6} my="auto">
                  <AddToCalendarSession
                    mutateSession={mutateSession}
                    color="black"
                    timeZone={timeZone}
                    session={session}
                  />
                </Box>
              </Flex>
              <Flex w="full" justifyContent={{ base: 'space-between', lg: 'center' }} alignItems="center">
                <Text ml={{ base: 0, lg: 12 }} mr={16} mt={1}>
                  {format(new Date(startsAt), 'HH:mm')} - {format(new Date(endsAt), 'HH:mm')}
                </Text>
                <Text flex={1} mr={{ base: 0, lg: 'auto' }}>
                  {session.venue}
                </Text>
                <Flex flex={1} mx="auto" justifyContent="center" alignItems="center">
                  {isLive && !isMobile && (
                    <Fragment>
                      <Box mr={2} rounded="full" w={3} h={3} bgColor="red.500" />
                      <Text fontFamily="heading" fontWeight="bold" color="red.500">
                        live
                      </Text>
                    </Fragment>
                  )}
                </Flex>
              </Flex>
            </Flex>
            {isLive && isMobile && (
              <Flex alignItems="center" mt={2}>
                <Box mr={2} rounded="full" w={3} h={3} bgColor="red.500" />
                <Text fontFamily="heading" fontWeight="bold" color="red.500">
                  live
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent borderRadius={0} border="none" boxShadow="0px 8px 24px 0px #00000033" w="100%" p={4} maxW={400}>
          <Heading fontSize="xl">{session.name}</Heading>
          <ListItemTooltip session={session} />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
